import { addMonths, startOfMonth } from "date-fns";
import { useEffect, useRef, useState } from "react";
import type { CalendarBounds } from "./calendarUtils";

type UseCalendarFetchRangeOptions = {
  calendarMonth: Date;
  calendarBounds: CalendarBounds;
  reservationBoundsResolved: boolean;
  fetchedMonthsRange: { from: Date | null; until: Date | null };
  fetchReservations: (options?: {
    month?: Date;
    past?: number;
    future?: number;
  }) => Promise<void>;
};

export default function useCalendarFetchRange({
  calendarMonth,
  calendarBounds,
  reservationBoundsResolved,
  fetchedMonthsRange,
  fetchReservations,
}: UseCalendarFetchRangeOptions) {
  const [calendarLoadingRange, setCalendarLoadingRange] = useState<{
    from: Date;
    until: Date;
  } | null>(null);
  const didInitialCalendarFetchRef = useRef(false);
  const calendarFetchTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!didInitialCalendarFetchRef.current) {
      didInitialCalendarFetchRef.current = true;
      return;
    }
    if (calendarFetchTimeoutRef.current !== null) {
      window.clearTimeout(calendarFetchTimeoutRef.current);
    }
    const desiredStart = startOfMonth(addMonths(calendarMonth, -2));
    const desiredEnd = startOfMonth(addMonths(calendarMonth, 2));
    if (reservationBoundsResolved) {
      const currentMonthStart = startOfMonth(calendarMonth);
      if (
        (calendarBounds.minMonth &&
          currentMonthStart.getTime() < calendarBounds.minMonth.getTime()) ||
        (calendarBounds.maxMonth &&
          currentMonthStart.getTime() > calendarBounds.maxMonth.getTime())
      ) {
        setCalendarLoadingRange(null);
        return;
      }
    }
    if (fetchedMonthsRange.from && fetchedMonthsRange.until) {
      const rangeStart = startOfMonth(fetchedMonthsRange.from);
      const rangeEnd = startOfMonth(fetchedMonthsRange.until);
      if (desiredStart.getTime() < rangeStart.getTime()) {
        setCalendarLoadingRange({
          from: desiredStart,
          until: startOfMonth(addMonths(rangeStart, -1)),
        });
      } else if (desiredEnd.getTime() > rangeEnd.getTime()) {
        setCalendarLoadingRange({
          from: startOfMonth(addMonths(rangeEnd, 1)),
          until: desiredEnd,
        });
      } else {
        setCalendarLoadingRange(null);
        return;
      }
    } else {
      setCalendarLoadingRange({ from: desiredStart, until: desiredEnd });
    }
    calendarFetchTimeoutRef.current = window.setTimeout(() => {
      fetchReservations({ month: calendarMonth });
      calendarFetchTimeoutRef.current = null;
    }, 250);
  }, [
    calendarMonth,
    calendarBounds.maxMonth,
    calendarBounds.minMonth,
    fetchReservations,
    fetchedMonthsRange.from,
    fetchedMonthsRange.until,
    reservationBoundsResolved,
  ]);

  useEffect(
    () => () => {
      if (calendarFetchTimeoutRef.current !== null) {
        window.clearTimeout(calendarFetchTimeoutRef.current);
      }
    },
    [],
  );

  return calendarLoadingRange;
}
